import { call, put, select } from 'redux-saga/effects';
import {
    getLiteTradeConfig,
    getLiteTradeSessionToken,
    getMarketDataCandleWatchers,
} from '../../../../modules/LiteTrade/liteTradeSelectors';
import { SOCKET_TYPE_MESSAGES, oppositeSubscriptionName } from '../../../../mobileAppCore/mobileAppConstants';
import {
    websocketCloseSubscriptionById,
    websocketOpenSubscriptionById,
    websocketTradingViewSubscribeAction,
    websocketTradingViewUnsubscribeAction,
} from '../../websocketReducer';
import { IMobileAppRootState } from '../../../../mobileAppCore/types/mobileAppState.t';
import { getUserSelectedAccountCode } from '../../../../app/appSelectors';
import _ from 'lodash';
import { updateMarketDataCandleAction } from '../../../../modules/LiteTrade/liteTradeActions';
import moment from 'moment';
import { ISOString } from '../../../../types';

export function* unsubscribeTradingViewData(action: ReturnType<typeof websocketTradingViewUnsubscribeAction>) {
    const { id } = action.payload;
    const liteTradeConfig = yield select(getLiteTradeConfig);
    const channel = liteTradeConfig?.wssMdUrl;
    yield put(websocketCloseSubscriptionById({ id, channel }));
}

function* subscriptionMarketDataCandleSaga(action: ReturnType<typeof websocketTradingViewSubscribeAction>) {
    const { requestId, symbols, candleType, fromTime, toTime, process } = action.payload;
    const liteTradeConfig = yield select(getLiteTradeConfig);
    const channel = liteTradeConfig?.wssMdUrl;
    const type = SOCKET_TYPE_MESSAGES.MARKET_DATA_OPEN;
    const sessionToken = yield select(getLiteTradeSessionToken);
    const selectedAccountCode = yield select(getUserSelectedAccountCode);
    if (channel) {
        yield put(
            websocketOpenSubscriptionById({
                id: requestId,
                channel,
                prev: {
                    sessionToken,
                    selectedAccountCode,
                    candleType,
                    fromTime,
                    toTime,
                    symbols,
                },
                open: function (state: IMobileAppRootState) {
                    const sessionToken = getLiteTradeSessionToken(state);
                    const selectedAccountCode = getUserSelectedAccountCode(state);

                    if (symbols.length === 0) {
                        return;
                    }

                    return {
                        requestId,
                        type,
                        payload: {
                            account: `default:${selectedAccountCode}`,
                            symbols,
                            eventTypes: [
                                {
                                    type: 'Candle',
                                    format: 'COMPACT',
                                    candleType,
                                    fromTime,
                                    toTime,
                                },
                            ],
                        },
                        session: sessionToken,
                    };
                },
                close: function (state: IMobileAppRootState) {
                    const sessionToken = getLiteTradeSessionToken(state);
                    return {
                        type: oppositeSubscriptionName[type],
                        refRequestId: requestId,
                        session: sessionToken,
                    };
                },
                process: function* (messageData: ReturnType<typeof JSON.parse>) {
                    yield call(process, messageData);
                },
            }),
        );
    }
}

export function* subscribeSymbolsDataWithCallbackSaga() {
    const marketDataCandleWatchers = yield select(getMarketDataCandleWatchers);
    const sessionToken = yield select(getLiteTradeSessionToken);
    const selectedAccountCode = yield select(getUserSelectedAccountCode);

    if (marketDataCandleWatchers && sessionToken && selectedAccountCode) {
        const symbols: string[] = _.uniq(marketDataCandleWatchers.map((item) => item.symbol));

        const requestId = `candle-${SOCKET_TYPE_MESSAGES.MARKET_DATA_OPEN}-${selectedAccountCode}`;
        const fromTime = moment().utcOffset(0, true).add(-1, 'week').startOf('day').toISOString() as ISOString;
        const toTime = moment().utcOffset(0, true).startOf('day').toISOString() as ISOString;
        const candleType = '1d';

        function* processSaga(messageData: ReturnType<typeof JSON.parse>) {
            if (messageData?.payload?.events && messageData.payload.events.length > 0) {
                yield put(updateMarketDataCandleAction(messageData.payload.events));
            }
        }
        yield put(
            websocketTradingViewSubscribeAction({
                requestId,
                process: processSaga,
                symbols,
                fromTime,
                toTime,
                candleType,
            }),
        );
    }
}


export default subscriptionMarketDataCandleSaga;
